import { pgTable, text, timestamp, integer, index, foreignKey, uniqueIndex } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { user } from './user';
import { file } from './files';
import { contentStatus, postDeactivationReason, privacyLevel, moderatableType } from './enums';
import { adminUser } from './admin';
import { teacher } from './content';


// ==========================================
// USER PROFILE - Extended profile settings
// ==========================================

export const userProfile = pgTable('UserProfile', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),

  // Privacy settings for each field
  emailPrivacy: privacyLevel().default('PRIVATE').notNull(),
  phonePrivacy: privacyLevel().default('PRIVATE').notNull(),
  aboutPrivacy: privacyLevel().default('PUBLIC').notNull(),
  socialLinksPrivacy: privacyLevel().default('PUBLIC').notNull(),

  // Denormalized counters for performance
  followersCount: integer().default(0).notNull(),
  followingCount: integer().default(0).notNull(),
  postsCount: integer().default(0).notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('UserProfile_userId_key').using('btree', table.userId.asc()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserProfile_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// USER FOLLOW - Follower/Following relationships
// ==========================================

export const userFollow = pgTable('UserFollow', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  followerId: integer().notNull(),
  followingId: integer().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('UserFollow_follower_following_key').using('btree', table.followerId.asc(), table.followingId.asc()),
  index('UserFollow_followerId_idx').using('btree', table.followerId.asc()),
  index('UserFollow_followingId_idx').using('btree', table.followingId.asc()),
  foreignKey({
    columns: [table.followerId],
    foreignColumns: [user.id],
    name: 'UserFollow_followerId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.followingId],
    foreignColumns: [user.id],
    name: 'UserFollow_followingId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// POST - User posts
// ==========================================

export const post = pgTable('Post', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  authorId: integer(),
  teacherId: integer(),

  // Content (TipTap HTML)
  content: text().notNull(),

  // Publication status
  status: contentStatus().default('DRAFT').notNull(),
  rejectionReason: text(),

  // Metrics
  viewsCount: integer().default(0).notNull(),
  likesCount: integer().default(0).notNull(),
  commentsCount: integer().default(0).notNull(),

  // Admin deletion info (soft-delete)
  deletionReason: text(),
  deletedBy: integer(), // Admin user ID
  deletedAt: timestamp({ precision: 3, mode: 'date' }),

  // Deactivation info (отдельно от soft-delete).
  // Деактивированный пост скрывается от пользователей, но остаётся видимым
  // админу и автору. Не может быть активирован обратно (например, когда
  // автор был удалён админом).
  deactivationReason: postDeactivationReason('deactivationReason'),
  deactivatedAt: timestamp({ precision: 3, mode: 'date' }),

  // Dates
  publishedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('Post_authorId_idx').using('btree', table.authorId.asc().nullsLast()),
  index('Post_teacherId_idx').using('btree', table.teacherId.asc().nullsLast()),
  index('Post_status_idx').using('btree', table.status.asc()),
  index('Post_publishedAt_idx').using('btree', table.publishedAt.desc().nullsLast()),
  index('Post_createdAt_idx').using('btree', table.createdAt.desc()),
  index('Post_status_publishedAt_idx').using('btree', table.status.asc(), table.publishedAt.desc().nullsLast()),
  index('Post_deletedAt_idx').using('btree', table.deletedAt.desc().nullsLast()),
  index('Post_deactivatedAt_idx').using('btree', table.deactivatedAt.desc().nullsLast()),
  foreignKey({
    columns: [table.authorId],
    foreignColumns: [user.id],
    name: 'Post_authorId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.teacherId],
    foreignColumns: [teacher.id],
    name: 'Post_teacherId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.deletedBy],
    foreignColumns: [adminUser.id],
    name: 'Post_deletedBy_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// POST IMAGE - Images in posts (up to 10)
// ==========================================

export const postImage = pgTable('PostImage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  postId: integer().notNull(),
  fileId: integer().notNull(),
  order: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('PostImage_postId_idx').using('btree', table.postId.asc()),
  index('PostImage_postId_order_idx').using('btree', table.postId.asc(), table.order.asc()),
  foreignKey({
    columns: [table.postId],
    foreignColumns: [post.id],
    name: 'PostImage_postId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.fileId],
    foreignColumns: [file.id],
    name: 'PostImage_fileId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// POST VIEW - Unique post views
// ==========================================

export const postView = pgTable('PostView', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  postId: integer().notNull(),
  userId: integer(),
  viewedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('PostView_postId_idx').using('btree', table.postId.asc()),
  index('PostView_userId_idx').using('btree', table.userId.asc().nullsLast()),
  uniqueIndex('PostView_postId_userId_key').using('btree', table.postId.asc(), table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.postId],
    foreignColumns: [post.id],
    name: 'PostView_postId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'PostView_userId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// COMMENT - Comments on posts/articles/lessons (tree)
// ==========================================

export const comment = pgTable('Comment', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  postId: integer(),
  articleId: integer(),
  moduleItemId: integer(),
  // XOR с senderAdminId: обычный комментарий — authorId (User), ответ
  // администрации — senderAdminId (AdminUser), authorId при этом NULL.
  authorId: integer(),
  senderAdminId: integer(),
  parentId: integer(),

  content: text().notNull(),

  // Moderation status
  status: contentStatus().default('PUBLISHED').notNull(),
  rejectionReason: text(),

  // Metrics
  likesCount: integer().default(0).notNull(),
  repliesCount: integer().default(0).notNull(),
  reportsCount: integer().default(0).notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('Comment_articleId_idx').using('btree', table.articleId.asc().nullsLast()),
  index('Comment_postId_idx').using('btree', table.postId.asc().nullsLast()),
  index('Comment_moduleItemId_idx').using('btree', table.moduleItemId.asc().nullsLast()),
  index('Comment_authorId_idx').using('btree', table.authorId.asc().nullsLast()),
  index('Comment_senderAdminId_idx').using('btree', table.senderAdminId.asc().nullsLast()),
  index('Comment_parentId_idx').using('btree', table.parentId.asc().nullsLast()),
  index('Comment_status_idx').using('btree', table.status.asc()),
  index('Comment_reportsCount_idx').using('btree', table.reportsCount.desc()),
  index('Comment_postId_status_createdAt_idx').using('btree', table.postId.asc(), table.status.asc(), table.createdAt.asc()),
  index('Comment_moduleItemId_status_createdAt_idx').using('btree', table.moduleItemId.asc(), table.status.asc(), table.createdAt.asc()),
  foreignKey({
    columns: [table.postId],
    foreignColumns: [post.id],
    name: 'Comment_postId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.authorId],
    foreignColumns: [user.id],
    name: 'Comment_authorId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  // moduleItemId FK добавляется отдельной SQL миграцией (0094) — держим
  // стиль, как у bookId на ModuleItem (FK только в SQL). Self-reference
  // parentId FK тоже добавляется в миграции.
  foreignKey({
    columns: [table.senderAdminId],
    foreignColumns: [adminUser.id],
    name: 'Comment_senderAdminId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// LIKE - Likes on posts and comments (polymorphic)
// ==========================================

export const like = pgTable('Like', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),

  // Polymorphic relationship (XOR)
  postId: integer(),
  commentId: integer(),
  articleId: integer(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('Like_articleId_idx').using('btree', table.articleId.asc().nullsLast()),
  uniqueIndex('Like_userId_articleId_key').using('btree', table.userId.asc(), table.articleId.asc().nullsLast()),
  uniqueIndex('Like_userId_postId_key').using('btree', table.userId.asc(), table.postId.asc().nullsLast()),
  uniqueIndex('Like_userId_commentId_key').using('btree', table.userId.asc(), table.commentId.asc().nullsLast()),
  index('Like_postId_idx').using('btree', table.postId.asc().nullsLast()),
  index('Like_commentId_idx').using('btree', table.commentId.asc().nullsLast()),
  index('Like_userId_idx').using('btree', table.userId.asc()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Like_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.postId],
    foreignColumns: [post.id],
    name: 'Like_postId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.commentId],
    foreignColumns: [comment.id],
    name: 'Like_commentId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// Note: ProductReview, ReviewImage, ReviewLike, and ProductRatingAggregate have been moved to commerce.ts
// as polymorphic Review, ReviewImage, ReviewLike, and RatingAggregate tables

// ==========================================
// MODERATION QUEUE - Content moderation queue
// ==========================================

export const moderationQueue = pgTable('ModerationQueue', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Polymorphic relationship
  entityType: moderatableType().notNull(),
  entityId: integer().notNull(),

  // Moderator
  moderatorId: integer(),

  // Status
  status: text().default('PENDING').notNull(),
  rejectionReason: text(),

  // Metadata
  submittedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  reviewedAt: timestamp({ precision: 3, mode: 'date' }),
}, (table) => [
  index('ModerationQueue_status_idx').using('btree', table.status.asc()),
  index('ModerationQueue_entityType_entityId_idx').using('btree', table.entityType.asc(), table.entityId.asc()),
  index('ModerationQueue_moderatorId_idx').using('btree', table.moderatorId.asc().nullsLast()),
  index('ModerationQueue_submittedAt_idx').using('btree', table.submittedAt.desc()),
  foreignKey({
    columns: [table.moderatorId],
    foreignColumns: [adminUser.id],
    name: 'ModerationQueue_moderatorId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// CONTENT REPORT - User reports on comments/posts
// ==========================================

export const contentReport = pgTable('ContentReport', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  reporterId: integer().notNull(),

  // Polymorphic relationship
  entityType: moderatableType().notNull(), // POST, COMMENT, REVIEW
  entityId: integer().notNull(),

  // Report details
  reason: text().notNull(),
  description: text(),

  // Resolution
  status: text().default('PENDING').notNull(), // PENDING, REVIEWING, RESOLVED, DISMISSED
  resolutionReason: text(),

  // Admin info
  reviewedBy: integer(), // Admin user ID
  resolvedAt: timestamp({ precision: 3, mode: 'date' }),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('ContentReport_reporterId_idx').using('btree', table.reporterId.asc()),
  index('ContentReport_entityType_entityId_idx').using('btree', table.entityType.asc(), table.entityId.asc()),
  index('ContentReport_status_idx').using('btree', table.status.asc()),
  index('ContentReport_createdAt_idx').using('btree', table.createdAt.desc()),
  index('ContentReport_status_createdAt_idx').using('btree', table.status.asc(), table.createdAt.desc()),
  foreignKey({
    columns: [table.reporterId],
    foreignColumns: [user.id],
    name: 'ContentReport_reporterId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.reviewedBy],
    foreignColumns: [adminUser.id],
    name: 'ContentReport_reviewedBy_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// MODERATION LOG - Audit log for admin actions
// ==========================================

export const moderationLog = pgTable('ModerationLog', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  adminId: integer().notNull(),

  // Action info
  action: text().notNull(), // APPROVE, REJECT, DELETE, RESTORE
  entityType: moderatableType().notNull(), // POST, COMMENT, REVIEW
  entityId: integer().notNull(),

  // Details
  reason: text(),
  details: text(), // JSON metadata

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('ModerationLog_adminId_idx').using('btree', table.adminId.asc()),
  index('ModerationLog_entityType_entityId_idx').using('btree', table.entityType.asc(), table.entityId.asc()),
  index('ModerationLog_action_idx').using('btree', table.action.asc()),
  index('ModerationLog_createdAt_idx').using('btree', table.createdAt.desc()),
  foreignKey({
    columns: [table.adminId],
    foreignColumns: [adminUser.id],
    name: 'ModerationLog_adminId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);
